import React, { useEffect, useState } from "react";
import axios from "axios";
import { Megaphone, ArrowRight, X } from "lucide-react";
import { getBreakingNewsColor } from "../lib/breakingNewsColors";

const API = `${window.__BACKEND__}/api`;
// Session-local dismiss key so users who close it don't see it for the session
const DISMISS_KEY_PREFIX = "vdh_breaking_news_dismissed_";

/**
 * BreakingNewsBar — slim red announcement strip displayed below the hero on the
 * homepage. Pulls the single active item from `/api/breaking-news/active`. If
 * no active item exists or the user has dismissed this specific item in the
 * current session, the component renders nothing.
 *
 * Visual: red gradient, pulsing animated megaphone, message, CTA pill (when
 * a link is present). Marquee fallback on small screens.
 */
export default function BreakingNewsBar() {
  const [item, setItem] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/breaking-news/active`)
      .then((r) => {
        if (!alive) return;
        const it = r.data?.item || null;
        setItem(it);
        if (it?.id) {
          try {
            const flag = sessionStorage.getItem(DISMISS_KEY_PREFIX + it.id);
            if (flag === "1") setDismissed(true);
          } catch {
            /* sessionStorage may be blocked */
          }
        }
      })
      .catch(() => {
        /* silent — bar simply won't show */
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!item || dismissed) return null;

  const palette = getBreakingNewsColor(item.color);
  const isExternal = /^https?:\/\//i.test(item.link_url || "");
  const close = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY_PREFIX + item.id, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <aside
      role="region"
      aria-label="Annuncio in evidenza"
      data-testid="breaking-news-bar"
      className="relative mx-3 sm:mx-5 lg:mx-8 -mt-3 sm:mt-2 md:mt-4"
    >
      <div
        className="relative overflow-hidden rounded-xl md:rounded-2xl"
        style={{
          background: `linear-gradient(95deg, ${palette.dark} 0%, ${palette.mid} 50%, ${palette.light} 100%)`,
          boxShadow: `0 0 0 1px rgba(255,255,255,0.10), 0 14px 38px -16px ${palette.shadow}, inset 0 1px 0 rgba(255,255,255,0.16)`,
        }}
      >
        {/* Decorative diagonal stripes */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.10] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, rgba(255,255,255,0.85) 0 2px, transparent 2px 14px)",
          }}
        />
        {/* Soft glow blob */}
        <div
          aria-hidden
          className="absolute -top-16 -right-10 w-64 h-64 rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "#ffdadc" }}
        />

        <div className="relative flex items-center gap-2 md:gap-5 px-2.5 md:px-6 py-1.5 md:py-3.5">
          {/* Label + animated megaphone */}
          <div className="flex-none flex items-center gap-1.5 md:gap-2.5 md:pr-3 md:border-r md:border-white/25">
            <span className="vdh-megaphone-wrap relative inline-flex items-center justify-center w-7 h-7 md:w-9 md:h-9 rounded-lg md:rounded-xl bg-white/10 backdrop-blur-sm">
              <Megaphone size={14} strokeWidth={2.4} className="text-white vdh-megaphone-shake md:hidden" />
              <Megaphone size={18} strokeWidth={2.4} className="text-white vdh-megaphone-shake hidden md:block" />
              <span aria-hidden className="vdh-megaphone-wave vdh-megaphone-wave-1" />
              <span aria-hidden className="vdh-megaphone-wave vdh-megaphone-wave-2" />
              <span aria-hidden className="vdh-megaphone-wave vdh-megaphone-wave-3" />
            </span>
            <span className="hidden md:inline-flex font-tech text-[11px] uppercase tracking-[0.28em] text-white/90">
              Ultima Ora
            </span>
          </div>

          {/* Message — 2 lines max on mobile with ellipsis */}
          <div
            className="flex-1 min-w-0 text-white font-display leading-tight md:leading-snug text-[11.5px] sm:text-[13.5px] md:text-[16.5px] vdh-news-clamp md:vdh-news-no-clamp"
            data-testid="breaking-news-message"
          >
            {item.message}
          </div>

          {/* CTA */}
          {item.link_url && (
            isExternal ? (
              <a
                href={item.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-none inline-flex items-center gap-1 md:gap-1.5 px-2.5 md:px-4 py-1 md:py-2 rounded-full bg-white hover:bg-white/90 transition-colors font-bold text-[10.5px] md:text-[13px] shadow-md whitespace-nowrap"
                style={{ color: palette.dark }}
                data-testid="breaking-news-cta"
              >
                {item.link_label || "Scopri"}
                <ArrowRight size={12} className="md:hidden" />
                <ArrowRight size={14} className="hidden md:block" />
              </a>
            ) : (
              <a
                href={item.link_url}
                className="flex-none inline-flex items-center gap-1 md:gap-1.5 px-2.5 md:px-4 py-1 md:py-2 rounded-full bg-white hover:bg-white/90 transition-colors font-bold text-[10.5px] md:text-[13px] shadow-md whitespace-nowrap"
                style={{ color: palette.dark }}
                data-testid="breaking-news-cta"
              >
                {item.link_label || "Scopri"}
                <ArrowRight size={12} className="md:hidden" />
                <ArrowRight size={14} className="hidden md:block" />
              </a>
            )
          )}

          {/* Dismiss */}
          <button
            type="button"
            onClick={close}
            aria-label="Chiudi annuncio"
            className="flex-none w-6 h-6 md:w-7 md:h-7 -mr-0.5 md:-mr-1 rounded-full text-white/85 hover:text-white hover:bg-white/15 flex items-center justify-center transition-colors"
            data-testid="breaking-news-close"
          >
            <X size={12} className="md:hidden" />
            <X size={14} className="hidden md:block" />
          </button>
        </div>
      </div>
    </aside>
  );
}
